'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { X, BookOpen, Calendar, Loader2 } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

type Story = {
  id: string;
  title: string;
  displayName: string;
  category: string;
  categoryLabel: string;
  submittedAt: string;
  excerpt: string;
  fullText: string;
  hasPhoto: boolean;
  photoSrc: string;
  photoAlt: string;
};

const CATEGORY_LABELS: Record<string, string> = {
  library: 'Library memories',
  wartime: 'Wartime memories',
  industrial: 'Industrial history',
  family: 'Family / Personal memories',
  photographs: 'Photographs',
  documents: 'Documents',
};

const ALL_CATEGORIES = [
  { value: 'all', label: 'All stories' },
  { value: 'library', label: 'Library memories' },
  { value: 'wartime', label: 'Wartime memories' },
  { value: 'industrial', label: 'Industrial history' },
  { value: 'family', label: 'Family / Personal' },
  { value: 'photographs', label: 'Photographs' },
  { value: 'documents', label: 'Documents' },
];

export default function CommunityStoriesContent() {
  const supabase = createClient();
  const [activeCategory, setActiveCategory] = useState('all');
  const [expandedStory, setExpandedStory] = useState<Story | null>(null);
  const [stories, setStories] = useState<Story[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStories();
  }, []);

  const fetchStories = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('stories')
        .select(`
          id,
          title,
          body_text,
          category,
          submitted_at,
          user_profiles!stories_user_id_fkey (display_name),
          story_photos (image_url, caption, display_order)
        `)
        .eq('status', 'approved')
        .order('submitted_at', { ascending: false });

      if (error) {
        console.error('Stories fetch error (PGRST code:', error.code, '):', error.message, '| Details:', error.details, '| Hint:', error.hint);
        setStories([]);
        return;
      }

      const mapped: Story[] = (data || []).map((row: any) => {
        const photos = row.story_photos || [];
        const firstPhoto = photos.sort((a: any, b: any) => a.display_order - b.display_order)[0];
        const bodyText = row.body_text || '';
        const excerpt = bodyText.length > 220 ? bodyText.slice(0, 220).trim() + '…' : bodyText;

        return {
          id: row.id,
          title: row.title,
          displayName: row.user_profiles?.display_name || 'Anonymous',
          category: row.category,
          categoryLabel: CATEGORY_LABELS[row.category] || row.category,
          submittedAt: new Date(row.submitted_at).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          }),
          excerpt,
          fullText: bodyText,
          hasPhoto: !!firstPhoto,
          photoSrc: firstPhoto?.image_url || '',
          photoAlt: firstPhoto?.caption || `Photo submitted with story: ${row.title}`,
        };
      });

      setStories(mapped);
    } catch (err) {
      console.log('Unexpected error fetching stories:', err);
      setStories([]);
    } finally {
      setLoading(false);
    }
  };

  const filtered = activeCategory === 'all'
    ? stories
    : stories.filter((s) => s.category === activeCategory);

  return (
    <>
      {/* Page header */}
      <section className="pt-28 pb-12 px-6 bg-secondary">
        <div className="max-w-screen-xl mx-auto">
          <p className="font-sans text-xs font-semibold uppercase tracking-[0.25em] text-accent mb-4">
            Community
          </p>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <h1 className="font-serif text-5xl md:text-6xl font-semibold text-foreground leading-tight mb-4">
                Community Stories
              </h1>
              <p className="font-sans text-lg text-muted-foreground max-w-2xl leading-relaxed">
                Memories, photographs and documents shared by people with a connection to Coed Parc. Every story helps build a richer picture of the building&apos;s history.
              </p>
            </div>
            <div className="shrink-0">
              <Link href="/share-your-story" className="btn-accent whitespace-nowrap">
                Share Your Story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Category filters */}
      <section className="px-6 py-6 border-b border-border bg-card">
        <div className="max-w-screen-xl mx-auto">
          <div className="flex flex-wrap gap-2">
            {ALL_CATEGORIES.map((cat) => (
              <button
                key={`filter-${cat.value}`}
                onClick={() => setActiveCategory(cat.value)}
                className={`font-sans text-sm font-medium px-4 py-2 rounded-sm border transition-colors ${
                  activeCategory === cat.value
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-primary hover:text-primary'
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Stories grid */}
      <section className="py-16 px-6">
        <div className="max-w-screen-xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 size={32} className="animate-spin text-primary" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen size={24} className="text-muted-foreground" />
              </div>
              <h2 className="font-serif text-2xl font-semibold text-foreground mb-3">
                No stories in this category yet
              </h2>
              <p className="font-sans text-base text-muted-foreground mb-8 max-w-md mx-auto">
                Be the first to share a memory, photograph or document in this category.
              </p>
              <Link href="/share-your-story" className="btn-primary">
                Share Your Story
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {filtered.map((story) => (
                <article
                  key={story.id}
                  className="bg-card border border-border rounded-sm overflow-hidden card-hover flex flex-col"
                >
                  {story.hasPhoto ? (
                    <div className="aspect-[16/9] relative overflow-hidden">
                      <AppImage
                        src={story.photoSrc}
                        alt={story.photoAlt}
                        fill
                        className="object-cover sepia-photo"
                        sizes="(max-width: 768px) 100vw, (max-width: 1280px) 50vw, 33vw"
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] photo-placeholder">
                      <div className="flex flex-col items-center gap-2">
                        <BookOpen size={28} className="text-muted-foreground/40" />
                        <p className="font-sans text-xs text-muted-foreground/50 italic">No photograph submitted</p>
                      </div>
                    </div>
                  )}

                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="category-badge">{story.categoryLabel}</span>
                    </div>
                    <h2 className="font-serif text-xl font-semibold text-foreground leading-snug mb-3">
                      {story.title}
                    </h2>
                    <p className="font-sans text-sm leading-relaxed text-muted-foreground mb-5 flex-1">
                      {story.excerpt}
                    </p>
                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-border">
                      <div className="flex flex-col gap-1">
                        <p className="font-sans text-sm font-medium text-foreground">{story.displayName}</p>
                        <div className="flex items-center gap-1.5 text-muted-foreground">
                          <Calendar size={12} />
                          <span className="font-sans text-xs">{story.submittedAt}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => setExpandedStory(story)}
                        className="btn-outline text-xs px-4 py-2"
                      >
                        Read more
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Story modal */}
      {expandedStory && (
        <>
          <div
            className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm animate-fade-in"
            onClick={() => setExpandedStory(null)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8">
            <div className="bg-card border border-border rounded-sm shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin animate-fade-up">
              <div className="flex items-center justify-between px-6 py-4 border-b border-border">
                <span className="category-badge">{expandedStory.categoryLabel}</span>
                <button
                  onClick={() => setExpandedStory(null)}
                  className="p-1.5 text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors"
                  aria-label="Close story"
                >
                  <X size={18} />
                </button>
              </div>
              {expandedStory.hasPhoto && (
                <div className="aspect-[16/9] relative overflow-hidden">
                  <AppImage
                    src={expandedStory.photoSrc}
                    alt={expandedStory.photoAlt}
                    fill
                    className="object-cover sepia-photo"
                    sizes="(max-width: 768px) 100vw, 672px"
                  />
                </div>
              )}
              <div className="px-6 py-6">
                <h2 className="font-serif text-2xl font-semibold text-foreground leading-snug mb-3">
                  {expandedStory.title}
                </h2>
                <div className="flex items-center gap-4 pb-5 border-b border-border mb-5">
                  <p className="font-sans text-sm font-medium text-foreground">{expandedStory.displayName}</p>
                  <div className="flex items-center gap-1.5 text-muted-foreground">
                    <Calendar size={12} />
                    <span className="font-sans text-xs">{expandedStory.submittedAt}</span>
                  </div>
                </div>
                <div>
                  {expandedStory.fullText.split('\n\n').map((para, i) => (
                    <p key={`modal-p-${i}`} className="font-sans text-sm leading-relaxed text-muted-foreground mb-4">
                      {para}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}